import { useState } from "react";
import { useSession } from "@clerk/react";
import { Button, Calendar, DateField, DatePicker, Input, Label, TextArea, TextField, toast } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { CalendarIcon, Check, Clock, Map, MapPin, Send, Users, X } from "lucide-react";

import GooglePlacesSearchDropdown from "@/components/maps/location-search";
import TimeWheelInline from "@/components/v2/time-wheel/time-wheel-inline";
import ParticipantsStep from "@/components/desktop/create-meets/comps/participants-step";
import { getReadableTimeDiff } from "@/utils/dates";
import { updateMeet as updateMeetApi } from "@/api/meets";

const Section = ({ icon: Icon, title, description, children }) => (
  <section className="flex flex-col gap-3 rounded-2xl">
    <div className="flex items-start gap-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Icon size={16} />
      </div>
      <div className="flex flex-col">
        <span className="font-semibold">{title}</span>
        {description && <span className="text-xs text-muted">{description}</span>}
      </div>
    </div>
    <div className="ps-12">{children}</div>
  </section>
);

const DesktopEditMeetsComp = ({ record, onCancel, onSaved }) => {
  const { session } = useSession();

  const [draft, setDraft] = useState(record);
  const [saving, setSaving] = useState(false);

  const updateDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const startTime = draft.start_time?.set ?? "";
  const endTime = draft.end_time?.set ?? "";

  const handleDateChange = (dateValue) => {
    if (!dateValue) return;
    updateDraft({ meeting_date: dateValue.toString() });
  };

  const handleStartTimeChange = (time) => updateDraft({ start_time: { ...draft.start_time, set: time } });

  const handleEndTimeChange = (time) => updateDraft({ end_time: { ...draft.end_time, set: time } });

  const handleLocationSelect = (place) => {
    updateDraft({
      location: {
        name: place["name"],
        place_id: place["place_id"],
        lat: place["lat"],
        lng: place["lng"],
      },
    });
  };

  const participants = (draft.members ?? []).map((m) => ({
    id: m.member_id,
    fullname: m.member_fullname,
    email_address: m.member_email,
  }));

  const handleParticipantsChange = (patch) => {
    if (!patch.participants) return;
    updateDraft({
      members: patch.participants.map((p) => ({
        member_id: p.id,
        member_email: p.email_address,
        member_fullname: p.fullname,
      })),
    });
  };

  const { lat, lng } = draft.location ?? {};
  const locationName = draft.location?.name;
  const hasLocation = Boolean(locationName && locationName !== "Not Set");

  const staticMapUrl =
    lat != null && lng != null
      ? `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=600x300&scale=2&markers=color:0x5855A0%7C${lat},${lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
      : null;

  const validate = () => {
    if (!draft.title?.trim()) {
      toast.danger("Title missing", { description: "Give the loop a name." });
      return false;
    }
    if (!draft.meeting_date) {
      toast.danger("Date missing", { description: "Pick a date for this loop." });
      return false;
    }
    if (!startTime || !endTime) {
      toast.danger("Time missing", { description: "Set both a start and end time." });
      return false;
    }
    // String comparison works on "HH:MM" and catches the common inversion.
    if (endTime <= startTime) {
      toast.danger("End time is before the start", { description: "Check the times and try again." });
      return false;
    }
    if ((draft.members ?? []).length === 0) {
      toast.danger("No participants", { description: "Add at least one person to this loop." });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const token = await session.getToken();

      const memberIds = (draft.members ?? []).map((m) => (typeof m === "string" ? m : m.member_id));
      const hostId = record?.created_by?.user_id;
      const members = hostId && !memberIds.includes(hostId) ? [...memberIds, hostId] : memberIds;

      const payload = { ...draft, members };
      const _resp = await updateMeetApi(payload, token);

      if (_resp.data.Result !== "SUCCESS") {
        throw new Error(_resp.data?.Message || "Update failed");
      }

      toast.success("Loop updated");
      onSaved?.(_resp.data.Record ?? payload);
    } catch (err) {
      console.error(err);
      toast.danger("Couldn't save changes", { description: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface text-foreground">
      <header className="flex shrink-0 items-start justify-between gap-6 border-b border-separator px-6 py-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-bold tracking-tight">Edit loop</h3>
          <p className="text-sm text-muted">Update the details, schedule, or who's joining.</p>
        </div>

        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          disabled={saving}
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-default hover:text-foreground"
        >
          <X size={18} />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-6">
        <Section icon={CalendarIcon} title="Details" description="Name and description">
          <div className="flex flex-col gap-4">
            <TextField name="title" value={draft.title ?? ""} onChange={(value) => updateDraft({ title: value })}>
              <Label>Title</Label>
              <Input placeholder="What's this loop about?" />
            </TextField>

            <TextField
              name="description"
              value={draft.description ?? ""}
              onChange={(value) => updateDraft({ description: value })}
            >
              <Label>Description</Label>
              <TextArea placeholder="Add any context for participants" rows={3} />
            </TextField>
          </div>
        </Section>

        <div className="h-px bg-separator" />

        <Section icon={Clock} title="Date & time" description="When the loop runs">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Date</Label>
              <DatePicker
                name="date"
                value={draft.meeting_date ? parseDate(draft.meeting_date) : null}
                onChange={handleDateChange}
                className="shadow-none"
              >
                <DateField.Group fullWidth className="h-10 rounded-xl border">
                  <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
                  <DateField.Suffix>
                    <DatePicker.Trigger className="border border-separator shadow-none">
                      <DatePicker.TriggerIndicator />
                    </DatePicker.Trigger>
                  </DateField.Suffix>
                </DateField.Group>
                <DatePicker.Popover>
                  <Calendar aria-label="Loop date">
                    <Calendar.Header>
                      <Calendar.YearPickerTrigger>
                        <Calendar.YearPickerTriggerHeading />
                        <Calendar.YearPickerTriggerIndicator />
                      </Calendar.YearPickerTrigger>
                      <Calendar.NavButton slot="previous" />
                      <Calendar.NavButton slot="next" />
                    </Calendar.Header>
                    <Calendar.Grid>
                      <Calendar.GridHeader>
                        {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                      </Calendar.GridHeader>
                      <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
                    </Calendar.Grid>
                    <Calendar.YearPickerGrid>
                      <Calendar.YearPickerGridBody>
                        {({ year }) => <Calendar.YearPickerCell year={year} />}
                      </Calendar.YearPickerGridBody>
                    </Calendar.YearPickerGrid>
                  </Calendar>
                </DatePicker.Popover>
              </DatePicker>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="flex items-center gap-1">
                <Clock size={14} />
                <span>Duration: {startTime && endTime ? getReadableTimeDiff(startTime, endTime) : "—"}</span>
              </Label>

              <div className="flex items-center gap-2">
                <TimeWheelInline value={startTime} onSelect={handleStartTimeChange} minuteStep={15} />
                <span className="text-muted">–</span>
                <TimeWheelInline value={endTime} onSelect={handleEndTimeChange} minuteStep={15} />
              </div>
            </div>
          </div>
        </Section>

        <div className="h-px bg-separator" />

        <Section icon={MapPin} title="Location" description="Where it's happening">
          <div className="flex flex-col gap-4">
            <GooglePlacesSearchDropdown
              defaultValue={hasLocation ? locationName : ""}
              placeholder="Type an address or place"
              inputClassName="h-10 rounded-xl border shadow-none"
              onSelect={handleLocationSelect}
            />

            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-1">
                <Map size={14} />
                Map preview
              </Label>
              {staticMapUrl ? (
                <div className="relative">
                  <img
                    src={staticMapUrl}
                    alt={locationName || "Selected location"}
                    className="h-40 w-full rounded-xl object-cover"
                  />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold text-accent shadow"
                  >
                    <Send size={12} /> Open in Maps
                  </a>
                </div>
              ) : (
                <div className="flex h-40 items-center justify-center rounded-xl bg-surface-secondary text-xs text-muted">
                  No location selected yet
                </div>
              )}
            </div>
          </div>
        </Section>

        <div className="h-px bg-separator" />

        <Section icon={Users} title="Participants" description="Who's joining this loop">
          <ParticipantsStep formData={{ participants }} updateFormData={handleParticipantsChange} />
        </Section>
      </div>

      <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-separator px-6 py-4">
        <Button variant="outline" onPress={onCancel} isDisabled={saving} className="h-10 font-bold">
          <X className="size-3.5" />
          Cancel
        </Button>

        <Button
          onPress={handleSave}
          isPending={saving}
          isDisabled={saving}
          className="h-10 min-w-[132px] font-bold shadow-md shadow-accent/30 dark:shadow-none"
        >
          {saving ? (
            "Saving..."
          ) : (
            <>
              <Check className="size-3.5" />
              Save changes
            </>
          )}
        </Button>
      </footer>
    </div>
  );
};

export default DesktopEditMeetsComp;